import S, { DataSignal } from 's-js';
import SArray from 's-array';
import { tzmoment } from 'lib/tzmoment';
import * as _ from 'underscore';
import { IAjax } from 'lib/Ajax';
import { callAll } from 'lib/callAll';
import TZDate from 'lib/TZDate';
import { DataCacheCollection } from 'lib/DataCache';
import { CaseInsensitiveContains } from 'lib/diacriticals';
import { Entrypoints } from 'core/app/entrypoints';
import { BerthReservationStatus } from 'server/enums/BerthReservation';
import { ReservationsDashboardRow } from 'data/models/ReservationsDashboardRow';
import { BookReservationsRequestData } from 'data/models/BookReservationsRequest';
import { CancelReservationsRequestData } from 'data/models/CancelReservationsRequest';
import { Organization } from 'data/models/Organization';
import { toCSVDataUri, CSVField, ExcelDateFormat, ExcelTimeFormat } from 'common/services/csvExport';
import { GridColumn, RowSorter, RowSelector } from 'portcall/gridcomponents/model';
import { DateRangeSelector, DateRangeSelectorParams, DateRange } from 'portcall/daterangeselector/model';
import { debounceSignal } from 'lib/debounceSignal';

// TH: TODO: Localization!!
const DATE_FORMAT = 'MM/DD/YY [at] HH:mm';

const
    VesselName : GridColumn<RowModel> = {
        Title: 'Vessel',
        DisplayValue: row => row.VesselName(),
        SortValue: row => row.VesselName(),
        ClassName: 'vessel'
    },
    BerthName : GridColumn<RowModel> = {
        Title: 'Berth',
        DisplayValue: row => row.BerthName(),
        SortValue: row => row.BerthName(),
        ClassName: 'berth'
    },
    PilotTime : GridColumn<RowModel> = {
        Title: 'Pilot Time',
        DisplayValue: row => row.PilotTimeDisplay(),
        SortValue: row => row.PilotTime() || new TZDate(0),
        ClassName: 'pilot-time'
    },
    FirstLine : GridColumn<RowModel> = {
        Title: 'First Line',
        DisplayValue: row => row.FirstLineDisplay(),
        SortValue: row => row.FirstLine() || new TZDate(0),
        ClassName: 'first-line'
    },
    LastLine : GridColumn<RowModel> = {
        Title: 'Last Line',
        DisplayValue: row => row.LastLineDisplay(),
        SortValue: row => row.LastLine() || new TZDate(0),
        ClassName: 'last-line'
    },
    Status : GridColumn<RowModel> = {
        Title: 'Status',
        DisplayValue: row => row.StatusText(),
        SortValue: row => row.StatusText(),
        ClassName: 'status'
    },
    RequestingAgency : GridColumn<RowModel> = {
        Title: 'Agency',
        DisplayValue: row => row.AgencyName(),
        SortValue: row => row.AgencyName(),
        ClassName: 'agency'
    },
    VoyageNumber : GridColumn<RowModel> = {
        Title: 'Voyage Number',
        DisplayValue: row => row.VoyageNumber(),
        SortValue: row => row.VoyageNumber(),
        ClassName: 'voyage-number'
    },
    PortName : GridColumn<RowModel> = {
        Title: 'Port/Region',
        DisplayValue: row => row.PortName(),
        SortValue: row => row.PortName(),
        ClassName: 'port'
    },
    PortCallId : GridColumn<RowModel> = {
        Title: 'PortCall Id',
        DisplayValue: row => row.PortCallIdDisplay(),
        SortValue: row=> row.PortCallId() || 0,
        ClassName: 'pc-id'
    };

const DefaultSortColumn = FirstLine;

export const Columns = [
    VesselName,
    BerthName,
    PilotTime,
    FirstLine,
    LastLine,
    Status,
    RequestingAgency,
    VoyageNumber,
    PortName
];

const CSVColumns : CSVField<RowModel>[] = [{
    name: VesselName.Title,
    formatter: VesselName.DisplayValue
}, {
    name: BerthName.Title,
    formatter: BerthName.DisplayValue
}, {
    name: PilotTime.Title,
    formatter: row => row.PilotTime() ? ExcelTimeFormat(row.PilotTime()!) : ''
}, {
    name: 'First Line (Date)',
    formatter: row => row.FirstLine() ? ExcelDateFormat(row.FirstLine()!) : ''
}, {
    name: 'First Line (Time)',
    formatter: row => row.FirstLine() ? ExcelTimeFormat(row.FirstLine()!) : ''
}, {
    name: 'Last Line (Date)',
    formatter: row => row.LastLine() ? ExcelDateFormat(row.LastLine()!) : ''
}, {
    name: 'Last Line (Time)',
    formatter: row => row.LastLine() ? ExcelTimeFormat(row.LastLine()!) : ''
}, {
    name: Status.Title,
    formatter: Status.DisplayValue
}, {
    name: RequestingAgency.Title,
    formatter: RequestingAgency.DisplayValue
}, {
    name: VoyageNumber.Title,
    formatter: VoyageNumber.DisplayValue
}, {
    name: PortName.Title,
    formatter: PortName.DisplayValue
}, {
    name: PortCallId.Title,
    formatter: PortCallId.DisplayValue
}];

export const enum PageAction {
    View, // No action selected. View rows.
    Book, // Select rows to be Booked.
    Cancel // Select rows to be Cancelled.
}

// TH: Not using lib/resultType b/c it doesn't let us generate generic types
export interface ReservationsListBase<TModel> {
    Model : () => TModel;
    Organization : () => Organization | null;
    Rows : () => RowModel[];
    RowSelector : RowSelector;
    DateRangeSelector : DateRangeSelector;
    Filter : DataSignal<string>;
    Loading : () => boolean;
    BookingOrCancelling : () => boolean;
    RowSorter: RowSorter<RowModel>;
    AnyBookableRows : () => boolean;
    AnyCancellableRows : () => boolean;
    ShowActionsMenu : DataSignal<boolean>;
    PageAction : DataSignal<PageAction>;
    SelectRowsToBook : () => void;
    BookSelectedReservations : () => void;
    SelectRowsToCancel : () => void;
    PromptForCancellationRemarks : () => void;
    CancellationRemarksPrompt : () => CancellationRemarksPrompt | null;
    CSVExportLink : () => string;
    GoBack : () => void;
}

export interface RowModel {
    Model : ReservationsDashboardRow;
    ShowActionsMenu : DataSignal<boolean>;
    SelectedForPageAction : DataSignal<boolean>;
    PortCallId : () => number | null;
    PortCallIdDisplay: () => string;
    VesselName : () => string;
    BerthName : () => string;
    PilotTime : () => TZDate | null;
    PilotTimeDisplay : () => string;
    FirstLine : () => TZDate | null;
    FirstLineDisplay : () => string;
    LastLine : () => TZDate | null;
    LastLineDisplay : () => string;
    Status : () => BerthReservationStatus | null;
    StatusText : () => string;
    AgencyName : () => string;
    VoyageNumber : () => string;
    PortName : () => string;
    Bookable : () => boolean;
    Cancellable : () => boolean;
    FilterData : () => string;
}

export interface ReservationsListBaseParams<TModel, TOptions> {
    cacheCollection : DataCacheCollection<TModel, TOptions>;
    url : (key : TOptions) => string;
    filterStorageKey : string;
    modelKey : (key : ServerKeyBase) => TOptions;
    organizationRef : (model : () => TModel) => () => Organization | null,
    range : DateRange;
    allowedRanges? : DateRange[];
    from? : TZDate;
    to? : TZDate;
    filter? : string;
}

export interface ServerReservationsListBase {
    Rows : () => ReservationsDashboardRow[];
}

export interface ServerKeyBase {
    FromDate : string;
    ToDate : string;
}

export function ReservationsListBase<TModel extends ServerReservationsListBase, TOptions> (
    ajax : IAjax,
    entrypoints : Entrypoints,
    dateRangeSelectorModel : (params : DateRangeSelectorParams) => DateRangeSelector,
    {
        cacheCollection,
        url,
        filterStorageKey,
        modelKey,
        organizationRef,
        range,
        allowedRanges,
        from,
        to,
        filter: _filter
    } : ReservationsListBaseParams<TModel, TOptions>
) : ReservationsListBase<TModel> {
    const dateRangeSelector = dateRangeSelectorModel({ range, allowedRanges, from, to }),
        filter = S.data(_filter || localStorage[filterStorageKey] as string | undefined || ''),
        loading = S.data(true),
        bookingOrCancelling = S.data(false),
        key = () => modelKey({
            FromDate: dateRangeSelector.FromDate().toJSON(),
            ToDate: dateRangeSelector.ToDate().toJSON(),
        }),
        model = S(() => cacheCollection.update1(null, key())),
        rowSorter = RowSorter(DefaultSortColumn),
        debouncedFilter = debounceSignal(filter),
        pageAction = S.data(PageAction.View),
        rows = SArray<ReservationsDashboardRow>([]),
        rowModels = rows.map(RowModel),
        anyBookableRows = rowModels.some(row => row.Bookable() && row.Model.CanBook()),
        anyCancellableRows = rowModels.some(row => row.Cancellable() && row.Model.CanCancel()),
        filteredRows = rowModels
            .filter(row => pageAction() === PageAction.View
                || pageAction() === PageAction.Book && row.Bookable() && row.Model.CanBook()
                || pageAction() === PageAction.Cancel && row.Cancellable() && row.Model.CanCancel())
            .filter(row => CaseInsensitiveContains(row.FilterData(), debouncedFilter() || '')),
        sortedRows = filteredRows.sort(rowSorter.RowOrder),
        rowSelector = RowSelector(sortedRows, row => row.SelectedForPageAction),
        showActionsMenu = S.data(false),
        csvExportLink = S(() => toCSVDataUri(sortedRows(), CSVColumns)),
        showCancellationRemarksPrompt = S.value(false),
        cancellationRemarksPrompt = S(() => showCancellationRemarksPrompt() ?
            CancellationRemarksPrompt(
                CancelSelectedReservations,
                pageAction,
                () => showCancellationRemarksPrompt(false)
            ) :
            null
        );

    // Store filter
    S(() => {
        localStorage[filterStorageKey] = filter();
    });

    // Fetch data
    S(() => {
        loading(true);
        let req = ajax.Url<{}, TOptions>(url(key())).Get();
        req.then(json => {
            cacheCollection.update1(req.Date, json);
        }).finally(() => loading(false));
    });

    // Populate data
    S(() => rows(model().Rows()));

    // Clear all row selections when either the filter or the page action changes
    S.on(callAll(filteredRows, pageAction), () => rowModels().forEach(row => row.SelectedForPageAction(false)), null, true);

    return {
        Model: model,
        Organization: organizationRef(model),
        Rows: sortedRows,
        RowSelector: rowSelector,
        DateRangeSelector: dateRangeSelector,
        Filter: filter,
        Loading: loading,
        BookingOrCancelling: bookingOrCancelling,
        RowSorter: rowSorter,
        AnyBookableRows: anyBookableRows,
        AnyCancellableRows: anyCancellableRows,
        ShowActionsMenu: showActionsMenu,
        PageAction: pageAction,
        SelectRowsToBook,
        BookSelectedReservations,
        SelectRowsToCancel,
        PromptForCancellationRemarks,
        CancellationRemarksPrompt: cancellationRemarksPrompt,
        CSVExportLink: csvExportLink,
        GoBack: () => window.history.back()
    };

    function SelectRowsToBook() {
        S.freeze(() => {
            showActionsMenu(false);
            pageAction(PageAction.Book);
        });
    }

    function SelectRowsToCancel() {
        S.freeze(() => {
            showActionsMenu(false);
            pageAction(PageAction.Cancel);
        });
    }

    function BookSelectedReservations() {
        const reservationIds = SelectedReservationIds();
        // Prevent double submits and submitting empty requests
        if (bookingOrCancelling() || !reservationIds.length) return;
        bookingOrCancelling(true);
        return ajax.Url<BookReservationsRequestData>(entrypoints.BookReservations, { useCache: true })
            .Post({ ReservationIds: reservationIds })
            .then(() => { pageAction(PageAction.View); })
            .finally(() => bookingOrCancelling(false));
    }

    function CancelSelectedReservations(remarks : string | null) {
        const reservationIds = SelectedReservationIds();
        // Prevent double submits and submitting empty requests
        if (bookingOrCancelling() || !reservationIds.length) return;
        bookingOrCancelling(true);
        return ajax.Url<CancelReservationsRequestData>(entrypoints.CancelReservations, { useCache: true })
            .Post({ ReservationIds: reservationIds, Remarks: remarks || null })
            .then(() => { pageAction(PageAction.View); })
            .finally(() => bookingOrCancelling(false));
    }

    function SelectedReservationIds() {
        return filteredRows().filter(row => row.SelectedForPageAction()).map(row => row.Model.Id());
    }

    function PromptForCancellationRemarks() {
        if (!bookingOrCancelling() && SelectedReservationIds().length) {
            showCancellationRemarksPrompt(true);
        }
    }

    function RowModel(row : ReservationsDashboardRow) : RowModel {
        const showActionsMenu = S.data(false),
            selectedForPageAction = S.data(false),
            pc = () => row.Reservation() && row.Reservation()!.Itinerary() && row.Reservation()!.Itinerary()!.PortCall(),
            loc = () => row.Reservation() && row.Reservation()!.Itinerary() && row.Reservation()!.Itinerary()!.Location(),
            portCallId = () => row.Reservation() && row.Reservation()!.Itinerary()
                ? row.Reservation()!.Itinerary()!.PortCallId() : null,
            portCallIdDisplay = S(() => portCallId()?.toString() ?? '--'),
            vesselName = () => pc() ? pc()!.DisplayVesselIdentifier() || '--' : '--',
            berthName = () => loc() ? loc()!.Name() : '--',
            pilotTime = () => {
                const _pc = pc();
                if (_pc) {
                    const portArrival = _pc.Itinerary().find(pt => pt.IsPortArrival());
                    if (!portArrival) return null;
                    return portArrival.Departure.Effective();
                }
                return null;
            },
            pilotTimeDisplay = S(() => pilotTime() ? tzmoment(pilotTime()!).format(DATE_FORMAT) : '--'),
            firstLine = () => row.Reservation() && row.Reservation()!.Itinerary()
                ? row.Reservation()!.Itinerary()!.Arrival.Effective() : null,
            firstLineDisplay = S(() => firstLine() ? tzmoment(firstLine()!).format(DATE_FORMAT) : '--'),
            lastLine = () => row.Reservation() && row.Reservation()!.Itinerary()
                ? row.Reservation()!.Itinerary()!.Departure.Effective() : null,
            lastLineDisplay = S(() => lastLine() ? tzmoment(lastLine()!).format(DATE_FORMAT) : '--'),
            status = () => row.Reservation() ? row.Reservation()!.Status() : null,
            statusText = () => row.Reservation() ?
                    row.Reservation()!.Status() === BerthReservationStatus.Cancelled ? 'Cancelled' :
                    row.Reservation()!.Itinerary()
                        && row.Reservation()!.Itinerary()!.Arrival.Actual()
                        && row.Reservation()!.Itinerary()!.Departure.Actual() ? 'Closed' :
                    row.Reservation()!.Status() === BerthReservationStatus.Conflicted ? 'Conflicted' :
                    row.Reservation()!.Status() === BerthReservationStatus.Booked ? 'Booked' :
                    'Requested'
                    : '--',
            agencyName = () => pc() && pc()!.Agent() && pc()!.Agent()!.Organization()
                ? pc()!.Agent()!.Organization()!.Name() : '--',
            voyageNumber = () => pc() ? pc()!.VoyageNumber() || '--' : '--',
            portName = () => loc() && loc()!.Berth() && loc()!.Berth()!.PierGroup() && loc()!.Berth()!.PierGroup()!.Port()
                ? loc()!.Berth()!.PierGroup()!.Port()!.Name() : '--',
            bookable = () => row.Reservation() ? row.Reservation()!.Bookable() : false,
            cancellable = () => row.Reservation() ? row.Reservation()!.Cancellable() : false,
            filterData = () =>
                vesselName() + " " +
                berthName() + " " +
                pilotTimeDisplay() + " " +
                firstLineDisplay() + " " +
                lastLineDisplay() + " " +
                statusText() + " " +
                agencyName() + " " +
                voyageNumber() + " " +
                portName();

        return {
            Model: row,
            ShowActionsMenu: showActionsMenu,
            SelectedForPageAction: selectedForPageAction,
            PortCallId: portCallId,
            PortCallIdDisplay: portCallIdDisplay,
            VesselName: vesselName,
            BerthName: berthName,
            PilotTime: pilotTime,
            PilotTimeDisplay: pilotTimeDisplay,
            FirstLine: firstLine,
            FirstLineDisplay: firstLineDisplay,
            LastLine: lastLine,
            LastLineDisplay: lastLineDisplay,
            Status: status,
            StatusText: statusText,
            AgencyName: agencyName,
            VoyageNumber: voyageNumber,
            PortName: portName,
            Bookable: bookable,
            Cancellable: cancellable,
            FilterData: filterData
        };
    }
}

export type CancellationRemarksPrompt = ReturnType<typeof CancellationRemarksPrompt>;
function CancellationRemarksPrompt(
    doCancel : (remarks : string | null) => PromiseLike<void> | undefined,
    pageAction : DataSignal<PageAction>,
    dismissPrompt : () => void,
) {
    const Remarks = S.value<string | null>(null);

    return {
        Remarks,
        CancelReservations,
        QuitChanges
    };

    function CancelReservations() {
        dismissPrompt();
        doCancel(Remarks());
    }

    function QuitChanges() {
        S.freeze(() => {
            dismissPrompt();
            pageAction(PageAction.View);
        });
    }
}



// WEBPACK FOOTER //
// ./apps/portcall/reservationslistbase/model.ts